import { useCallback, useState } from "react";
import axios from "axios";
import { Api } from "services/api/api.service";
import { AbsenceType } from "model/Schedule";
import type { Result } from "utils/result";
import type { TimeReportErrorKey } from "hooks/timeReportErrors";
import { resolveAlertKeyFromApiError } from "hooks/timeReportErrors";

export interface ReportAbsencePayload {
  companyId: number;
  timeReportId: number;
  comment: string;
  resolution: AbsenceType;
}

export interface UseReportAbsenceResult {
  reportAbsence: (
    payload: ReportAbsencePayload,
  ) => Promise<Result<void, TimeReportErrorKey>>;
  isLoading: boolean;
  error: TimeReportErrorKey | null;
}

export const useReportAbsence = (): UseReportAbsenceResult => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<TimeReportErrorKey | null>(null);

  const reportAbsence = useCallback(
    async (
      payload: ReportAbsencePayload,
    ): Promise<Result<void, TimeReportErrorKey>> => {
      setIsLoading(true);
      setError(null);
      try {
        await Api().company.timeReports.reportAbsence(
          payload.companyId,
          payload.timeReportId,
          payload.comment,
          payload.resolution,
        );
        return { ok: true, value: undefined };
      } catch (err: unknown) {
        const alertKey = axios.isAxiosError(err)
          ? resolveAlertKeyFromApiError(err)
          : "Alerts.UnexpectedError";
        setError(alertKey);
        return { ok: false, error: alertKey };
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return { reportAbsence, isLoading, error };
};
